import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { Toaster } from './components/ui/sonner';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SimulationsHubPage from './pages/SimulationsHubPage';
import MessagesPage from './pages/MessagesPage';
import RapportsPage from './pages/RapportsPage';
import ReferentielsPage from './pages/ReferentielsPage';
import CroquisAgenceurPage from './pages/CroquisAgenceurPage';
import DashboardCommercialPage from './pages/DashboardCommercialPage';
import CapexSalesPage from './pages/CapexSalesPage';
import AccountDetailPage from './pages/AccountDetailPage';
import NewSimulationPage from './pages/NewSimulationPage';
import SimulationResultPage from './pages/SimulationResultPage';
import SimulationComparePage from './pages/SimulationComparePage';
import HistoryPage from './pages/HistoryPage';
import AdminPage from './pages/AdminPage';
import CockpitPage from './pages/CockpitPage';
import PerformanceCommerciauxPage from './pages/PerformanceCommerciauxPage';
import CapexHGPage from './pages/CapexHGPage';
import ApprovisionnementCCDPage from './pages/ApprovisionnementCCDPage';
import CaCornerPage from './pages/CaCornerPage';
import CapexAssistantPage from './pages/CapexAssistantPage';
import CarteCornersPage from './pages/CarteCornersPage';
import ProfilePage from './pages/ProfilePage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import GoldenInfoPage from './pages/GoldenInfoPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import ChantierListPage from './pages/ChantierListPage';
import ChantierFormPage from './pages/ChantierFormPage';
import DossiersAgenceurPage from './pages/DossiersAgenceurPage';
import PVLivraisonPage from './pages/PVLivraisonPage';
import PVTimelinePage from './pages/PVTimelinePage';
import PrerequisTechniquesListPage from './pages/PrerequisTechniquesListPage';
import PrerequisTechniquesFormPage from './pages/PrerequisTechniquesFormPage';
import AgenceurResponsePage from './pages/AgenceurResponsePage';
import ContractRequestsPage from './pages/ContractRequestsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ChantierSynthesePage from './pages/ChantierSynthesePage';
import GuidePage from './pages/GuidePage';
import PVLivraisonGuidePage from './pages/PVLivraisonGuidePage';
import PrimesPage from './pages/PrimesPage';
import PlanningChantiersPage from './pages/PlanningChantiersPage';
import BusinessBouillantDossierPage from './pages/BusinessBouillantDossierPage';
import { VersionWatcher } from './components/VersionWatcher';
import BusinessCommercialPage from './pages/BusinessCommercialPage';
import PresentationPage from './pages/PresentationPage';
import FormationPage from './pages/FormationPage';
import './index.css';

const ProtectedRoute = ({ children, blockPrestataire = false, allowedRoles = null }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (blockPrestataire && user?.role === 'prestataire') {
    return <Navigate to="/chantiers" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === 'prestataire') return <Navigate to="/chantiers" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const SmartRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'prestataire') return <Navigate to="/chantiers" replace />;
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      {/* Public Agenceur Response (no auth) */}
      <Route path="/agenceur/respond/:token" element={<AgenceurResponsePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute blockPrestataire>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulations"
        element={
          <ProtectedRoute blockPrestataire>
            <SimulationsHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute blockPrestataire>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapports"
        element={
          <ProtectedRoute blockPrestataire>
            <RapportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/referentiels"
        element={
          <ProtectedRoute blockPrestataire>
            <ReferentielsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/croquis-agenceur"
        element={
          <ProtectedRoute blockPrestataire>
            <CroquisAgenceurPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulations/tableau"
        element={
          <ProtectedRoute blockPrestataire>
            <DashboardPage view="simulations" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulations/dashboard-commercial"
        element={
          <ProtectedRoute blockPrestataire>
            <DashboardCommercialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation/new"
        element={
          <ProtectedRoute blockPrestataire>
            <NewSimulationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation/edit/:id"
        element={
          <ProtectedRoute blockPrestataire>
            <NewSimulationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulation/:id"
        element={
          <ProtectedRoute blockPrestataire>
            <SimulationResultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute blockPrestataire>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute blockPrestataire>
            <SimulationComparePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute blockPrestataire>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cockpit"
        element={
          <ProtectedRoute blockPrestataire>
            <CockpitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'commercial', 'commerciale']}>
            <PerformanceCommerciauxPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute blockPrestataire>
            <AnalyticsDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/golden-info"
        element={
          <ProtectedRoute blockPrestataire>
            <GoldenInfoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute blockPrestataire>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chantiers"
        element={
          <ProtectedRoute>
            <ChantierListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning-chantiers"
        element={
          <ProtectedRoute blockPrestataire>
            <PlanningChantiersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/planning-chantiers/presentation"
        element={
          <ProtectedRoute blockPrestataire>
            <PlanningChantiersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-bouillant/:id"
        element={
          <ProtectedRoute blockPrestataire allowedRoles={['admin', 'super_admin', 'commercial', 'commerciale']}>
            <BusinessBouillantDossierPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-commercial"
        element={
          <ProtectedRoute blockPrestataire allowedRoles={['admin', 'super_admin', 'commercial', 'commerciale']}>
            <BusinessCommercialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chantier/new"
        element={
          <ProtectedRoute>
            <ChantierFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chantier/:id"
        element={
          <ProtectedRoute>
            <ChantierSynthesePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chantier/:id/synthese"
        element={
          <ProtectedRoute>
            <ChantierSynthesePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chantier/:id/edit"
        element={
          <ProtectedRoute>
            <ChantierFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dossiers-agenceur"
        element={
          <ProtectedRoute>
            <DossiersAgenceurPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pv-livraison/:commandeId"
        element={
          <ProtectedRoute>
            <PVLivraisonPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pv-livraison"
        element={
          <ProtectedRoute>
            <PVTimelinePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/presentation"
        element={
          <ProtectedRoute>
            <PresentationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract-requests"
        element={
          <ProtectedRoute blockPrestataire>
            <ContractRequestsPage />
          </ProtectedRoute>
        }
      />

      {/* Prérequis Techniques */}
      <Route
        path="/prerequis-techniques"
        element={
          <ProtectedRoute>
            <PrerequisTechniquesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prerequis-techniques/:prereqId"
        element={
          <ProtectedRoute>
            <PrerequisTechniquesFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prerequis-techniques/simulation/:simulationId"
        element={
          <ProtectedRoute>
            <PrerequisTechniquesFormPage />
          </ProtectedRoute>
        }
      />

      {/* Guide */}
      <Route path="/guide" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />
      <Route path="/formation" element={<ProtectedRoute blockPrestataire><FormationPage /></ProtectedRoute>} />
      <Route path="/capex-hg" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><CapexHGPage /></ProtectedRoute>} />
      <Route path="/approvisionnement-ccd" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><ApprovisionnementCCDPage /></ProtectedRoute>} />
      <Route path="/ca-corner" element={<ProtectedRoute><CaCornerPage /></ProtectedRoute>} />
      <Route path="/assistant" element={<ProtectedRoute><CapexAssistantPage /></ProtectedRoute>} />
      <Route path="/carte-corners" element={<ProtectedRoute><CarteCornersPage /></ProtectedRoute>} />
      <Route path="/guide-pv" element={<ProtectedRoute><PVLivraisonGuidePage /></ProtectedRoute>} />
      {/* Primes */}
      <Route path="/primes" element={<ProtectedRoute><PrimesPage /></ProtectedRoute>} />
      <Route path="/capex-sales" element={<ProtectedRoute><CapexSalesPage /></ProtectedRoute>} />
      <Route path="/capex-sales/account/:id" element={<ProtectedRoute><AccountDetailPage /></ProtectedRoute>} />

      {/* Redirect */}
      <Route path="/" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
      <Route path="*" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  // Initialize theme from localStorage
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    }
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <PermissionsProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
            <VersionWatcher />
            <PwaInstallPrompt />
          </BrowserRouter>
        </PermissionsProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
